import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { trackStep } from "@/lib/traffic";
import { Lock, ArrowUpRight } from "lucide-react";

const cards = [
  {
    slug: "louer-vendre",
    over: "Patrimoine",
    title: "Continuer à louer ou vendre\u00A0?",
    img: "https://images.unsplash.com/photo-1666585607888-3f6fe0b323d8?w=900&q=70&auto=format",
  },
  {
    slug: "coloc-simple",
    over: "Revenus",
    title: "Colocation ou location simple\u00A0?",
    img: "https://images.unsplash.com/photo-1657129208507-7005dfc5b36a?w=600&q=70&auto=format",
  },
  {
    slug: "cash-flow",
    over: "Cash-flow",
    title: "Quel cash-flow pour votre investissement\u00A0?",
    short: "Quel cash-flow locatif\u00A0?",
    img: "https://static.prod-images.emergentagent.com/jobs/45a85651-67e9-4f6e-9a04-3abb6cfd3185/images/7c35d2481a173c565b955aa75f6f603e5d648e013ee15bfe0227b4912dc00edd.jpeg",
  },
  {
    slug: "tri-rendement",
    over: "Rentabilité",
    title: "TRI ou rendement locatif\u00A0?",
    img: "https://static.prod-images.emergentagent.com/jobs/45a85651-67e9-4f6e-9a04-3abb6cfd3185/images/eb1bda69d9b732994183f81fc65c25d646cc507498339a5b03664fe527660d0d.jpeg",
  },
];

function SimCard({ c, i, onClick }) {
  return (
    <button
      data-testid={`home-card-${c.slug}`}
      onClick={onClick}
      style={{ animationDelay: `${0.1 + i * 0.08}s` }}
      className="animate-fade-up relative aspect-square w-full min-w-0 overflow-hidden rounded-[22px] text-left group shadow-[0_10px_30px_-12px_rgba(35,28,24,0.45)] transition-transform duration-200 active:scale-[0.97]"
    >
      <img
        src={c.img}
        alt={c.title}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1C1613]/90 via-[#1C1613]/30 to-[#1C1613]/25" />
      <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
        <span className="px-2.5 py-1 rounded-full bg-black/35 backdrop-blur-md border border-white/20 text-[10px] font-bold tracking-[0.12em] uppercase text-white">
          {c.over}
        </span>
        <span className="shrink-0 h-8 w-8 rounded-full bg-[#C84C2C] text-white flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:rotate-45">
          <ArrowUpRight strokeWidth={2} className="h-4 w-4" />
        </span>
      </div>
      <h2 className="absolute inset-x-0 bottom-0 p-3.5 font-serif text-[15px] font-bold text-white leading-snug drop-shadow-sm">
        {c.short || c.title}
      </h2>
    </button>
  );
}

export default function Home() {
  useEffect(() => trackStep("accueil", "arrivee"), []);
  const navigate = useNavigate();

  return (
    <div className="flex flex-col min-h-screen px-5 pt-6 pb-10" data-testid="home-screen">
      <header className="flex items-center justify-between mb-8">
        <span className="text-xs font-bold tracking-[0.25em] text-[#C84C2C] font-sans">ASSERTIMMO</span>
        <button
          onClick={() => navigate("/admin")}
          data-testid="home-admin-lock"
          className="h-9 w-9 rounded-full flex items-center justify-center text-[#8C7A70] hover:text-[#231C18] hover:bg-[#F5EFE6] transition-colors"
          aria-label="Accès admin"
        >
          <Lock strokeWidth={1.5} className="h-4 w-4" />
        </button>
      </header>

      <div className="animate-fade-up">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 mb-4 rounded-full bg-[#FDF1EE] text-[#C84C2C] text-[11px] font-bold tracking-[0.12em] uppercase">
          <span className="h-1.5 w-1.5 rounded-full bg-[#E9B65A]" /> Simulateurs gratuits
        </span>
        <h1 className="font-serif text-4xl leading-[1.08] font-extrabold text-[#231C18]">
          Faites le <span className="text-[#C84C2C]">bon choix</span> pour votre patrimoine.
        </h1>
        <p className="mt-4 text-base text-[#8C7A70] leading-relaxed">
          Quatre simulateurs gratuits pour une réponse claire et chiffrée, en moins de deux minutes.
        </p>
      </div>

      <div className="mt-6 flex items-center gap-3" data-testid="home-sim-count">
        <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-[#231C18]">4 simulateurs</span>
        <span className="h-px flex-1 bg-[#E6DDD4]" />
      </div>

      <div className="mt-4 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3.5">
        {cards.map((c, i) => (
          <SimCard key={c.slug} c={c} i={i} onClick={() => navigate(`/simulateur/${c.slug}`)} />
        ))}
      </div>

      <p className="mt-auto pt-10 text-xs text-[#8C7A70] leading-relaxed">
        Outil d'aide à la décision. Les résultats sont des estimations basées sur vos hypothèses.
      </p>
      <a
        href="https://www.assertimmo.com/cgu-assertimmo"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="footer-cgu-link"
        className="mt-3 inline-block text-xs font-semibold text-[#C84C2C] underline underline-offset-2"
      >
        Politique de confidentialité
      </a>
    </div>
  );
}
